import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Briefcase, TrendingUp, Users, Award, Upload, ArrowRight, MapPin, DollarSign, X, FileText, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import CareerProgression from '../components/CareerProgression';
import SEO from '../components/SEO';
import { 
  PageHeader, 
  Section, 
  Container, 
  SectionHeader,
  FadeInOnScroll,
  PrimaryButton
} from '../components/DesignSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Job Card Component with expandable description
const JobCard = ({ job, onApply }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Get first 150 characters or first paragraph as preview
  const getPreviewText = (text) => {
    if (!text) return '';
    const firstParagraph = text.split('\n')[0];
    if (firstParagraph.length <= 150) return firstParagraph;
    return text.substring(0, 150).trim() + '...';
  };
  
  const previewText = getPreviewText(job.description);
  const hasMoreContent = job.description && job.description.length > 150;

  return (
    <div 
      className="group bg-white rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100/80 hover:border-blue-200/50"
      data-testid={`job-listing-${job.id}`}
    >
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{job.title}</h3>
          <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-4">
            <span className="flex items-center gap-2 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full font-medium">
              <Briefcase className="w-4 h-4" />
              {job.type}
            </span>
            <span className="flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full font-medium">
              <MapPin className="w-4 h-4" />
              {job.location}
            </span>
            {job.salary && (
              <span className="flex items-center gap-2 bg-green-50 text-green-600 px-3 py-1.5 rounded-full font-medium">
                <DollarSign className="w-4 h-4" />
                {job.salary}
              </span>
            )}
          </div>
          
          {/* Description with Read More/Less */}
          <div className="text-gray-600 leading-relaxed">
            {isExpanded ? (
              <p className="whitespace-pre-wrap">{job.description}</p>
            ) : (
              <p>{previewText}</p>
            )}
            
            {hasMoreContent && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="inline-flex items-center gap-1 text-blue-600 font-semibold mt-3 hover:text-blue-700 transition-colors"
              >
                {isExpanded ? (
                  <>
                    Read Less
                    <ChevronUp className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Read More
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        
        <div className="flex-shrink-0 lg:mt-0">
          <button 
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300"
            onClick={onApply}
          >
            Apply Now
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const Careers = () => {
  const { toast } = useToast();
  const [jobListings, setJobListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    message: ''
  });
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [cvUploaded, setCvUploaded] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchJobListings();
  }, []);

  const fetchJobListings = async () => {
    try {
      const response = await axios.get(`${API}/jobs?active_only=true`);
      setJobListings(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Build submission data including CV URL if uploaded
      const submissionData = {
        _subject: `FURD Job Application: ${formData.position}`,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        coverLetter: formData.message,
        cvUrl: cvUploaded?.url || 'Not provided',
        cvFilename: cvUploaded?.filename || 'Not provided',
        enquiryType: 'Job Application',
        submittedAt: new Date().toISOString()
      };

      // Submit to Formspree
      const response = await fetch('https://formspree.io/f/xnjbqppr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      
      if (response.ok) {
        // Also save to database for records
        try {
          await axios.post(`${API}/contact/submit`, {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            enquiryType: 'careers',
            message: `Job Application for: ${formData.position}\n\nCover Letter:\n${formData.message}\n\nCV: ${cvUploaded?.url || 'Not provided'}`
          });
        } catch (dbError) {
          console.log('Database save optional:', dbError);
        }
        
        toast({
          title: "Application Submitted!",
          description: "Thank you for your interest. We'll review your application and get back to you soon.",
        });
        setFormData({ name: '', email: '', phone: '', position: '', message: '' });
        setCvFile(null);
        setCvUploaded(null);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExtensions = ['.pdf', '.doc', '.docx'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF, DOC, or DOCX file.",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10MB.",
        variant: "destructive"
      });
      return;
    }

    setCvFile(file);
    setCvUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/cv/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setCvUploaded(response.data);
        toast({
          title: "CV Uploaded!",
          description: `${file.name} has been uploaded successfully.`,
        });
      }
    } catch (error) {
      console.error('CV upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload CV. Please try again.",
        variant: "destructive"
      });
      setCvFile(null);
    } finally {
      setCvUploading(false);
    }
  };

  const removeCv = () => {
    setCvFile(null);
    setCvUploaded(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Career Progression',
      description: 'Clear career pathways with opportunities for advancement and professional development'
    },
    {
      icon: Users,
      title: 'Collaborative Team',
      description: 'Work with experienced professionals in a supportive and collaborative environment'
    },
    {
      icon: Award,
      title: 'Professional Training',
      description: 'Continuous learning opportunities including chartership support and technical training'
    },
    {
      icon: Briefcase,
      title: 'Exciting Projects',
      description: 'Work on diverse projects from luxury retail to sustainable industrial facilities'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="careers-page">
      <SEO 
        title="Careers at FURD Ltd"
        description="Join our team of passionate engineers building a sustainable future through innovative MEP design."
      />
      
      {/* Hero Section */}
      <PageHeader
        badge="Join Our Team"
        badgeIcon={Briefcase}
        title="Careers at FURD Ltd"
        subtitle="Join our team of passionate engineers building a sustainable future through innovative MEP design."
      />

      {/* Why Join Us */}
      <Section background="white" padding="default">
        <Container>
          <SectionHeader
            badge="Benefits"
            title="Why Join"
            titleHighlight="FURD Ltd?"
            subtitle="Benefits and opportunities for professional growth"
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <FadeInOnScroll key={index} delay={index * 100}>
                  <div className="group h-full bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100/80 hover:border-blue-200/80 transform hover:-translate-y-2 text-center relative overflow-hidden">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-600/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    <div className="relative mb-5 inline-block">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity" />
                      <div className="relative w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-2 transition-all duration-500 shadow-lg mx-auto">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300 relative z-10">{benefit.title}</h3>
                    <p className="text-gray-500 leading-relaxed relative z-10">{benefit.description}</p>
                  </div>
                </FadeInOnScroll>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* Open Positions */}
      <Section background="light" padding="default">
        <Container>
          <SectionHeader
            badge="Opportunities"
            title="Current"
            titleHighlight="Openings"
            subtitle="Explore opportunities to join our team"
          />

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : jobListings.length === 0 ? (
            <FadeInOnScroll>
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Briefcase className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-500 text-xl font-medium mb-2">No current openings</p>
                <p className="text-gray-400">Check back soon or send us your CV!</p>
              </div>
            </FadeInOnScroll>
          ) : (
            <div className="space-y-6">
              {jobListings.map((job, index) => (
                <FadeInOnScroll key={job.id || job._id} delay={index * 100}>
                  <JobCard 
                    job={job} 
                    onApply={() => {
                      setFormData({...formData, position: job.title});
                      document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' });
                    }}
                  />
                </FadeInOnScroll>
              ))}
            </div>
          )}
        </Container>
      </Section>

      {/* Career Progression - Dynamic from CMS */}
      <CareerProgression showFullVersion={true} />

      {/* Application Form */}
      <Section background="gradient" padding="default" id="application-form">
        <Container>
          <div className="max-w-3xl mx-auto">
            <SectionHeader
              badge="Get Started"
              title="Apply"
              titleHighlight="Now"
              subtitle="Submit your application and we&apos;ll get back to you soon"
            />

            <FadeInOnScroll>
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl border border-gray-100">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="h-14 rounded-xl text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                      data-testid="career-name-input"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Email *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        required
                        className="h-14 rounded-xl text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                        data-testid="career-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Phone</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+44 (0) 20 1234 5678"
                        className="h-14 rounded-xl text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                        data-testid="career-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Position Applied For *</label>
                    <Input
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      placeholder="e.g., Senior MEP Engineer"
                      required
                      className="h-14 rounded-xl text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                      data-testid="career-position-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Cover Letter *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us why you'd like to join FURD Ltd..."
                      rows={6}
                      required
                      className="rounded-xl text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 transition-colors resize-none"
                      data-testid="career-message-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">CV / Resume</label>
                    {cvUploaded ? (
                      // Show uploaded file
                      <div className="border-2 border-green-200 bg-green-50 rounded-2xl p-6 relative">
                        <button
                          type="button"
                          onClick={removeCv}
                          className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-50 transition-colors"
                          data-testid="remove-cv-btn"
                        >
                          <X className="w-4 h-4 text-gray-500 hover:text-red-500" />
                        </button>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-900 font-semibold truncate">{cvUploaded.filename}</p>
                            <p className="text-sm text-green-600">Uploaded successfully ({cvUploaded.size_mb}MB)</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Show upload area
                      <div 
                        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 cursor-pointer ${
                          cvUploading 
                            ? 'border-blue-400 bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50/50 group'
                        }`}
                        onClick={() => !cvUploading && fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={handleCvUpload}
                          className="hidden"
                          data-testid="cv-file-input"
                        />
                        
                        {cvUploading ? (
                          <>
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                            <p className="text-blue-600 font-medium">Uploading {cvFile?.name}...</p>
                            <p className="text-sm text-blue-400 mt-1">Please wait</p>
                          </>
                        ) : (
                          <>
                            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-blue-100 transition-colors">
                              <Upload className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-colors" />
                            </div>
                            <p className="text-gray-600 font-medium">Click to upload your CV</p>
                            <p className="text-sm text-gray-400 mt-1">PDF, DOC, or DOCX (max 10MB)</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold h-14 text-lg rounded-xl shadow-lg hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="career-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </FadeInOnScroll>
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section background="dark" padding="default">
        <Container className="text-center relative z-10">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
          </div>
          
          <FadeInOnScroll>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">Ready to Make an Impact?</h2>
            <p className="text-lg md:text-xl text-blue-100/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              Join our team and help shape the future of sustainable building services engineering.
            </p>
            <PrimaryButton to="/contact" size="lg" icon={ArrowRight}>
              Get in Touch
            </PrimaryButton>
          </FadeInOnScroll>
        </Container>
      </Section>
    </div>
  );
};

export default Careers;
